import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';

export type SettingsOverviewStatus =
  | 'loading'
  | 'ready'
  | 'unauth'
  | 'empty'
  | 'error';

export type ViewerRoleSystemKey = 'owner' | 'admin' | 'member' | null;

export type SettingsCardKey =
  | 'function-roles'
  | 'permission-groups'
  | 'org-standards'
  | 'skills'
  | 'documents'
  | 'work-items'
  | 'billing'
  | 'organization'
  | 'organizations';

export interface SettingsCard {
  key: SettingsCardKey;
  title: string;
  description: string;
  to: string;
  ownerOnly: boolean;
  /** ISO timestamp of the most recent write across the sub-area's rows, or
   *  null when the sub-area has never been written (renders `Updated never`).
   *  In turn 3 (this index turn) all sub-features are pre-build, so the VM
   *  returns `null` for every card. Subsequent Phase-2 turns (5.x / 6.x / …)
   *  hydrate these from each sub-feature's own history table once the schema
   *  lands. */
  lastUpdatedAt: string | null;
  /** Handle of the user who last wrote to the sub-area, or null when
   *  `lastUpdatedAt` is null. Same hydration timeline. */
  lastUpdatedBy: string | null;
}

export interface UseSettingsOverviewVM {
  status: SettingsOverviewStatus;
  /** Org name for the org header strip. */
  activeOrgName: string | null;
  /** Active (non-deleted) member count for the org header strip. */
  memberCount: number;
  /** Document count for the org header strip. The `documents` table doesn't
   *  yet exist (Phase-3 turn 12.x lands it); this returns 0 until then so the
   *  strip can still render `0 documents` rather than blank. */
  documentCount: number;
  /** Caller's permission-role system_key in the active org. Drives card
   *  visibility — Owner sees all 6, Admin sees 5 (no Organization), Member
   *  sees 4 (no Permission groups, no Organization). */
  viewerRole: ViewerRoleSystemKey;
  /** Display string for the role chip (`Owner` / `Admin` / `Member`). */
  viewerRoleLabel: string | null;
  /** Cards filtered by the viewer's role. Ordered per the inventory in
   *  `../web-mock/features/settings/prd.md` § "Sub-area inventory". */
  cards: ReadonlyArray<SettingsCard>;
  error: string | null;
}

const ALL_CARDS: ReadonlyArray<
  Omit<SettingsCard, 'lastUpdatedAt' | 'lastUpdatedBy'> & {
    roles: ReadonlyArray<NonNullable<ViewerRoleSystemKey>>;
  }
> = [
  {
    key: 'function-roles',
    title: 'Function roles',
    description:
      'System + custom roles and the agent rules they load via MCP. One screen.',
    to: '/settings/function-roles',
    ownerOnly: false,
    roles: ['owner', 'admin', 'member']
  },
  {
    key: 'permission-groups',
    title: 'Permission groups',
    description: 'Capability matrix per permission group.',
    to: '/settings/permission-groups',
    ownerOnly: false,
    roles: ['owner', 'admin']
  },
  {
    key: 'org-standards',
    title: 'Org standards',
    description: 'Org-wide "how we work here" prose. Singleton per org.',
    to: '/settings/org-standards',
    ownerOnly: false,
    roles: ['owner', 'admin', 'member']
  },
  {
    key: 'skills',
    title: 'Skills',
    description:
      'Install skills from GitHub repos, pinned to a version. Toggle installed skills on or off.',
    to: '/settings/skills',
    ownerOnly: false,
    roles: ['owner', 'admin', 'member']
  },
  {
    key: 'documents',
    title: 'Document settings',
    description:
      'Types (PRD, Note, Plan, …) and the Property library for documents. One page, two tabs.',
    to: '/settings/documents',
    ownerOnly: false,
    roles: ['owner', 'admin', 'member']
  },
  {
    key: 'work-items',
    title: 'Work Item settings',
    description:
      'Types (Epic / Story / Bug / Task / Subtask), the Property library that backs them, and per-type status pipelines. One page, three tabs.',
    to: '/settings/work-items',
    ownerOnly: false,
    roles: ['owner', 'admin', 'member']
  },
  {
    key: 'billing',
    title: 'Billing',
    description:
      'Manage your plan, seats, payment method, and invoices.',
    to: '/settings/billing',
    ownerOnly: false,
    roles: ['owner', 'admin']
  },
  {
    key: 'organization',
    title: 'Organization',
    description: 'Rename org, transfer ownership, delete org.',
    to: '/settings/organization',
    ownerOnly: true,
    roles: ['owner']
  },
  {
    key: 'organizations',
    title: 'Organizations',
    description: 'All organizations you belong to. Switch context or create a new org.',
    to: '/settings/organizations',
    ownerOnly: false,
    roles: ['owner', 'admin', 'member']
  }
];

function roleLabelFor(key: ViewerRoleSystemKey): string | null {
  if (key === 'owner') return 'Owner';
  if (key === 'admin') return 'Admin';
  if (key === 'member') return 'Member';
  return null;
}

/**
 * ViewModel for `/settings` overview (turn 3). Reads:
 *
 * - The active org owned by the shell (see `useActiveOrg`). This VM is a pure
 *   consumer: it scopes every query to whatever org the shell says is active
 *   and re-loads when the user switches. It must NOT re-derive its own "oldest
 *   membership" org — that was the cross-org leak.
 * - The active org's name (from the shell context).
 * - The viewer's permission-role `system_key` for THIS org's membership.
 * - The org's active member count via a `head: true, count: 'exact'` query
 *   against `memberships` for the active org with `deleted_at IS NULL`.
 *
 * Returns the 6-card inventory filtered to those the viewer can read. No
 * mutations land in this turn — sub-areas ship their own VMs in turns 4–11.
 */
export function useSettingsOverviewVM(): UseSettingsOverviewVM {
  const [status, setStatus] = useState<SettingsOverviewStatus>('loading');
  const { activeOrgId, activeOrgName } = useActiveOrg();
  const [memberCount, setMemberCount] = useState(0);
  const [viewerRole, setViewerRole] = useState<ViewerRoleSystemKey>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setStatus('unauth');
        return;
      }

      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!activeOrgId) return;

      // Viewer's permission-role for THIS org's membership.
      const { data: membershipRow, error: memErr } = await supabase
        .from('memberships')
        .select('permission_role:permission_role_id(system_key)')
        .eq('user_id', session.user.id)
        .eq('org_id', activeOrgId)
        .maybeSingle();

      if (cancelled) return;
      if (memErr) {
        setStatus('error');
        setError("Couldn't load your settings. Try again.");
        return;
      }

      type RoleEmbed =
        | { system_key?: string | null }
        | { system_key?: string | null }[]
        | null;
      const memRow = membershipRow as { permission_role: RoleEmbed } | null;
      const sysKey = memRow
        ? Array.isArray(memRow.permission_role)
          ? memRow.permission_role[0]?.system_key ?? null
          : memRow.permission_role?.system_key ?? null
        : null;
      const role: ViewerRoleSystemKey =
        sysKey === 'owner' || sysKey === 'admin' || sysKey === 'member'
          ? sysKey
          : null;

      const { count, error: countErr } = await supabase
        .from('memberships')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', activeOrgId)
        .is('deleted_at', null);

      if (cancelled) return;
      if (countErr) {
        setStatus('error');
        setError("Couldn't load your settings. Try again.");
        return;
      }

      setMemberCount(count ?? 0);
      setViewerRole(role);
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId]);

  const cards: SettingsCard[] = viewerRole
    ? ALL_CARDS.filter((c) => c.roles.includes(viewerRole)).map((c) => ({
        key: c.key,
        title: c.title,
        description: c.description,
        to: c.to,
        ownerOnly: c.ownerOnly,
        lastUpdatedAt: null,
        lastUpdatedBy: null
      }))
    : [];

  return {
    status,
    activeOrgName,
    memberCount,
    documentCount: 0,
    viewerRole,
    viewerRoleLabel: roleLabelFor(viewerRole),
    cards,
    error
  };
}
